import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  Avatar,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import Layout from '../components/Layout';
import useStore from '../store/useStore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import ChatIcon from '@mui/icons-material/Chat';
import { translations } from '../translations';
import DashboardHero from '../components/dashboard/DashboardHero';
import { usePressFeedback, useStaggerReveal } from '../components/fx/motion';
import { orchard } from '../theme';

export default function ParentDashboard() {
  const [children, setChildren] = useState([]);
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgStudent, setMsgStudent] = useState(null);
  const [msgText, setMsgText] = useState('');
  const [msgSending, setMsgSending] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const { user, language } = useStore();
  const t = translations[language];
  const navigate = useNavigate();
  const pageRef = useRef(null);
  useStaggerReveal(pageRef);
  usePressFeedback(pageRef);

  useEffect(() => {
    fetch('/api/parents/dashboard', { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => {
        setChildren(data.children || []);
      });
  }, []);

  const handleSendMessage = async () => {
    if (!msgText.trim()) return;
    setMsgSending(true);
    try {
      const res = await fetch('/api/parents/message-teacher', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: msgText.trim(),
          studentId: msgStudent?._id || null,
        }),
      });
      if (res.ok) {
        setMsgText('');
        setMsgOpen(false);
        setSnack({
          open: true,
          message:
            language === 'ru'
              ? 'Сообщение отправлено учителю'
              : 'Message sent to teacher',
          severity: 'success',
        });
      } else {
        setSnack({
          open: true,
          message: 'Error sending message',
          severity: 'error',
        });
      }
    } catch {
      setSnack({
        open: true,
        message: 'Error sending message',
        severity: 'error',
      });
    } finally {
      setMsgSending(false);
    }
  };

  const en = language === 'en';

  return (
    <Layout title={t.parent}>
      <Container maxWidth="lg" ref={pageRef}>
        <DashboardHero
          eyebrow={en ? 'Parent portal' : 'Родительский портал'}
          title={`${t.welcome}, ${user?.name || user?.username}!`}
          subtitle={
            en
              ? "Welcome to your child's learning portal."
              : 'Добро пожаловать в учебный портал вашего ребенка.'
          }
          sceneDensity={0.8}
          actions={
            <Button
              variant="contained"
              disableElevation
              startIcon={<ChatIcon />}
              onClick={() => {
                setMsgStudent(null);
                setMsgText('');
                setMsgOpen(true);
              }}
              sx={{
                bgcolor: '#fff',
                // theme gives contained buttons a cherry gradient image
                backgroundImage: 'none',
                color: orchard.cherryDeep,
                fontWeight: 800,
                '&:hover': {
                  bgcolor: orchard.blossom,
                  backgroundImage: 'none',
                },
              }}
            >
              {en ? 'Message teacher' : 'Написать учителю'}
            </Button>
          }
        />

        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontSize: { xs: '1.1rem', sm: '1.3rem' },
          }}
        >
          <ChildCareIcon color="primary" /> {t.myChildren}
        </Typography>

        <Grid container spacing={1.5}>
          {children.length === 0 ? (
            <Grid size={12}>
              <Paper
                data-reveal
                variant="outlined"
                sx={{
                  p: { xs: 4, sm: 6 },
                  textAlign: 'center',
                  borderRadius: '24px',
                  borderStyle: 'dashed',
                  boxShadow: 'none',
                  bgcolor: 'action.hover',
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  {en
                    ? 'No children assigned yet. Please contact admin.'
                    : 'Дети не привязаны. Обратитесь к администратору.'}
                </Typography>
              </Paper>
            </Grid>
          ) : (
            children.map((child) => (
              <Grid size={{ xs: 12, sm: 6 }} key={child._id}>
                <Box
                  data-reveal
                  data-press
                  role="link"
                  tabIndex={0}
                  aria-label={child.name}
                  onClick={() => navigate(`/student/${child._id}`)}
                  onKeyDown={(e) => {
                    if (e.target !== e.currentTarget) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/student/${child._id}`);
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: { xs: 1.5, sm: 2 },
                    borderRadius: '20px',
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    WebkitTapHighlightColor: 'transparent',
                    transition: `border-color 0.25s ${orchard.ease}, box-shadow 0.25s ${orchard.ease}`,
                    '@media (hover: hover)': {
                      '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: '0 14px 30px -16px rgba(140,18,48,0.35)',
                      },
                    },
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: 2,
                    },
                  }}
                >
                  <Avatar
                    src={
                      child.mainPhotoId
                        ? `/api/photos/student/${child._id}/main?thumb=1`
                        : undefined
                    }
                    alt=""
                    sx={{
                      width: { xs: 52, sm: 64 },
                      height: { xs: 52, sm: 64 },
                      background: `linear-gradient(135deg, ${orchard.cherryBright}, ${orchard.cherryDeep})`,
                      color: '#fff',
                      fontSize: { xs: '1.3rem', sm: '1.6rem' },
                      fontWeight: 800,
                    }}
                  >
                    {child.name[0]}
                  </Avatar>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography
                      noWrap
                      sx={{
                        fontWeight: 800,
                        fontSize: { xs: '1.05rem', sm: '1.2rem' },
                      }}
                    >
                      {child.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {en
                        ? 'Tap to view reports & portfolio.'
                        : 'Нажмите, чтобы открыть отчёты и портфолио.'}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      gap: 0.5,
                    }}
                  >
                    <ChevronRightIcon color="action" />
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ChatIcon sx={{ fontSize: '1rem' }} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setMsgStudent(child);
                        setMsgText('');
                        setMsgOpen(true);
                      }}
                      sx={{ fontSize: '0.75rem', px: 1.25, minHeight: 36 }}
                    >
                      {en ? 'Message' : 'Написать'}
                    </Button>
                  </Box>
                </Box>
              </Grid>
            ))
          )}
        </Grid>

        {/* Message Teacher Dialog */}
        <Dialog
          open={msgOpen}
          onClose={() => setMsgOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            {language === 'ru' ? 'Написать учителю' : 'Message Teacher'}
            {msgStudent && (
              <Typography variant="body2" color="text.secondary">
                {language === 'ru' ? 'По ученику: ' : 'Regarding: '}
                {msgStudent.name}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder={
                language === 'ru'
                  ? 'Введите сообщение...'
                  : 'Type your message...'
              }
              value={msgText}
              onChange={(e) => setMsgText(e.target.value)}
              sx={{ mt: 1 }}
              autoFocus
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setMsgOpen(false)}>{t.cancel}</Button>
            <Button
              variant="contained"
              onClick={handleSendMessage}
              disabled={!msgText.trim() || msgSending}
              startIcon={
                msgSending ? <CircularProgress size={16} /> : <ChatIcon />
              }
            >
              {language === 'ru' ? 'Отправить' : 'Send'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snack.open}
          autoHideDuration={4000}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity={snack.severity}
            onClose={() => setSnack((s) => ({ ...s, open: false }))}
          >
            {snack.message}
          </Alert>
        </Snackbar>
      </Container>
    </Layout>
  );
}
